import React, { useState, useEffect } from 'react';
import { studentsWithNumbers } from '../../Data/studentLearningProfiles';

const SLPStudentSelector = ({ selectedStudents = [], onStudentsSelected, onCancel }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [tempSelected, setTempSelected] = useState(selectedStudents);

  // Get unique classes for filtering
  const classes = useEffect(() => {
    const classSet = new Set(studentsWithNumbers.map(student => student.class));
    return Array.from(classSet).sort();
  }, []);

  // Filter students based on search and class
  const filteredStudents = useEffect(() => {
    return studentsWithNumbers.filter(student => {
      const matchesSearch = !searchTerm || 
        student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.studentNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.class.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesClass = !selectedClass || student.class === selectedClass;
      
      return matchesSearch && matchesClass;
    });
  }, [searchTerm, selectedClass]);

  const handleStudentToggle = (studentNumber) => {
    setTempSelected(prev => {
      if (prev.includes(studentNumber)) {
        return prev.filter(s => s !== studentNumber);
      } else {
        return [...prev, studentNumber];
      }
    });
  };

  const handleSelectAll = () => {
    const allFilteredNumbers = filteredStudents.map(s => s.studentNumber);
    const allSelected = allFilteredNumbers.every(num => tempSelected.includes(num));
    
    if (allSelected) {
      // Deselect all filtered students
      setTempSelected(prev => prev.filter(s => !allFilteredNumbers.includes(s)));
    } else {
      // Select all filtered students
      const newSelected = [...new Set([...tempSelected, ...allFilteredNumbers])];
      setTempSelected(newSelected);
    }
  };

  const handleConfirm = () => {
    onStudentsSelected(tempSelected);
  };

  const isStudentSelected = (studentNumber) => tempSelected.includes(studentNumber);
  const selectedCount = tempSelected.length;
  const filteredCount = filteredStudents.length;
  const allFilteredSelected = filteredStudents.length > 0 && 
    filteredStudents.every(s => isStudentSelected(s.studentNumber));

  return (
    <div style={{ 
      padding: '20px', 
      backgroundColor: '#f8f9fa', 
      minHeight: '100vh',
      fontFamily: 'Arial, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '20px',
        padding: '15px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <button 
            onClick={onCancel}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              marginRight: '15px',
              color: '#007bff'
            }}
          >
            ← 
          </button>
          <div>
            <h2 style={{ margin: 0, color: '#333' }}>選擇學生</h2>
            <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '14px' }}>
              已選擇 {selectedCount} 位學生
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={onCancel}
            style={{
              padding: '10px 16px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            取消
          </button>
          
          <button
            onClick={handleConfirm}
            style={{
              padding: '10px 16px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            確認選擇 ({selectedCount})
          </button>
        </div>
      </div>

      {/* Search and Filter Controls */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        padding: '20px',
        marginBottom: '20px'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 200px 150px',
          gap: '15px',
          alignItems: 'end'
        }}>
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '5px', 
              fontWeight: 'bold', 
              color: '#333' 
            }}>
              搜尋學生
            </label>
            <input
              type="text"
              placeholder="輸入學生姓名、學號或班別..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>

          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '5px', 
              fontWeight: 'bold', 
              color: '#333' 
            }}>
              班別篩選
            </label>
            <select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              <option value="">所有班別</option>
              {classes.map(className => (
                <option key={className} value={className}>
                  {className}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={handleSelectAll}
            style={{
              padding: '10px 16px',
              backgroundColor: allFilteredSelected ? '#dc3545' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              whiteSpace: 'nowrap'
            }}
          >
            {allFilteredSelected ? '取消全選' : '全選當前'}
          </button>
        </div>

        {/* Filter Summary */}
        <div style={{
          marginTop: '15px',
          padding: '10px',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          fontSize: '14px',
          color: '#666'
        }}>
          顯示 {filteredCount} 位學生
          {selectedClass && ` (班別: ${selectedClass})`}
          {searchTerm && ` (搜尋: "${searchTerm}")`}
        </div>
      </div>

      {/* Student List */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {/* List Header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '50px 60px 200px 100px 120px 1fr',
          gap: '15px',
          padding: '15px',
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid #ddd',
          fontSize: '14px',
          fontWeight: 'bold',
          color: '#333'
        }}>
          <div>選擇</div>
          <div>照片</div>
          <div>姓名</div>
          <div>班別</div>
          <div>學號</div>
          <div>備註</div>
        </div>

        {/* Student Rows */}
        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
          {filteredStudents.length === 0 ? (
            <div style={{
              padding: '40px',
              textAlign: 'center',
              color: '#666'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '20px' }}>👥</div>
              <h3>找不到符合條件的學生</h3>
              <p>請調整搜尋條件或篩選設定</p>
            </div>
          ) : (
            filteredStudents.map((student, index) => (
              <div
                key={student.studentNumber}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '50px 60px 200px 100px 120px 1fr',
                  gap: '15px',
                  padding: '15px',
                  borderBottom: index < filteredStudents.length - 1 ? '1px solid #eee' : 'none',
                  backgroundColor: isStudentSelected(student.studentNumber) ? '#e3f2fd' : 'white',
                  cursor: 'pointer',
                  alignItems: 'center',
                  transition: 'background-color 0.2s'
                }}
                onClick={() => handleStudentToggle(student.studentNumber)}
                onMouseEnter={(e) => {
                  if (!isStudentSelected(student.studentNumber)) {
                    e.target.style.backgroundColor = '#f8f9fa';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isStudentSelected(student.studentNumber)) {
                    e.target.style.backgroundColor = 'white';
                  }
                }}
              >
                <div style={{ textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={isStudentSelected(student.studentNumber)}
                    onChange={() => {}}
                    style={{
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer'
                    }}
                  />
                </div>

                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: student.gender === 'M' ? '#e3f2fd' : '#fce4ec',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '18px',
                    margin: '0 auto'
                  }}>
                    {student.gender === 'M' ? '👦' : '👧'}
                  </div>
                </div>

                <div>
                  <div style={{ 
                    fontWeight: 'bold', 
                    fontSize: '15px',
                    color: '#333'
                  }}>
                    {student.name}
                  </div>
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#666',
                    marginTop: '2px'
                  }}>
                    {student.nameEn}
                  </div>
                </div>

                <div style={{
                  padding: '4px 8px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '12px',
                  fontSize: '12px',
                  textAlign: 'center',
                  color: '#333',
                  fontWeight: 'bold'
                }}>
                  {student.class}
                </div>

                <div style={{
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  color: '#666'
                }}>
                  {student.studentNumber}
                </div>

                <div style={{ 
                  fontSize: '12px', 
                  color: '#666'
                }}>
                  {student.dateOfBirth && `生日: ${student.dateOfBirth}`}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Selected Students Summary */}
      {selectedCount > 0 && (
        <div style={{
          marginTop: '20px',
          padding: '20px',
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ margin: '0 0 15px 0', color: '#333' }}>
            已選擇的學生 ({selectedCount} 人)
          </h3>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
            gap: '10px',
            maxHeight: '150px',
            overflowY: 'auto'
          }}>
            {tempSelected.map(studentNumber => {
              const student = studentsWithNumbers.find(s => s.studentNumber === studentNumber);
              if (!student) return null;
              
              return (
                <div
                  key={studentNumber}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    backgroundColor: '#e3f2fd',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  <span>
                    <strong>{student.name}</strong>
                    <span style={{ marginLeft: '8px', color: '#666' }}>
                      {student.class}
                    </span>
                  </span>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStudentToggle(studentNumber);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#dc3545',
                      cursor: 'pointer',
                      fontSize: '16px',
                      padding: '2px'
                    }}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default SLPStudentSelector; 